import React, { useCallback, useMemo, useRef } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { useNavigationHeaderOptions } from "../../common/navigationHeaderOptions";
import { ProductComponentQuery } from "./__generated__/ProductComponentQuery.graphql";
import { ExpireDatePicker } from "../ProductExpireBatchCreate/ExpireDatePicker";
import { ProductComponent_StoreProduct$key } from "./__generated__/ProductComponent_StoreProduct.graphql";
import { ProductExpireBatches } from "./ProductExpireBatches";
import { ProductStackScreenProps, ProductStack } from "./ProductStack";
import { ProductLinkedTodos } from "./ProductLinkedTodos";
import { ProductLinkedTodosFloatingAddButton } from "./ProductLinkedTodosFloatingAddButton";
import {
  AHEmptyScreen,
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  useLayoutContext,
} from "arkh";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import {
  DEVICE_HAS_SCANNER,
  ScanToAddExpireDate,
  ScanToOpenProductLabel,
} from "./ScanToAddExpireDate";
import { FormattedMessage } from "react-intl";
import { Image } from "expo-image";
import { useBarcodeScanner } from "../../common/useBarcodeScanner";
import { ProductScreenState } from "./ProductScreenState";
import { ProductDetails } from "./ProductDetails";
import { ProductHeader } from "./ProductHeader";
import { TabBar } from "./TabBar";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import Animated, {
  runOnJS,
  useAnimatedRef,
  useAnimatedScrollHandler,
} from "react-native-reanimated";
import { FloatingAddButton } from "./FloatingAddButton";
import { ProductComponent_Store$key } from "./__generated__/ProductComponent_Store.graphql";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View } from "react-native";

const STORE_PRODUCT_QUERY = graphql`
  query ProductComponentQuery(
    $id: ID!
    $ean: String!
    $isEan: Boolean = false
  ) {
    storeProduct(id: $id) @skip(if: $isEan) {
      ...ProductComponent_StoreProduct
    }
    currentStore @required(action: THROW) {
      ...ProductComponent_Store
      productByEAN(ean: $ean) @include(if: $isEan) {
        ...ProductComponent_StoreProduct
      }
    }
  }
`;

function InnerProductComponent(props: {
  state: ProductScreenState;
  setState: (state: ProductScreenState) => void;
  navigation: ProductStackScreenProps<"ProductMain">["navigation"];
  store: ProductComponent_Store$key;
  product: ProductComponent_StoreProduct$key;
  ean: string | null;
  id: string | null;
}) {
  const { state, setState, navigation } = props;
  const expireBatchesRef = useRef<{
    onEndReached: () => void;
  }>(null);
  const linkedTodosRef = useRef<{
    onEndReached: () => void;
  }>(null);

  const scrollRef = useAnimatedRef<Animated.ScrollView>();
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();
  const intl_tokens = useIntlTokens();
  const store = useFragment(
    graphql`
      fragment ProductComponent_Store on Store {
        ...ProductLinkedTodosFloatingAddButton_Store
      }
    `,
    props.store
  );
  const product = useFragment(
    graphql`
      fragment ProductComponent_StoreProduct on StoreProduct {
        id
        name
        imageUrl
        description
        partNumber
        ...ProductHeader_ProductDetails
        numberOfPendingTodos
        hasExpiredExpireDates
        hasPendingExpireDateActions
      }
    `,
    props.product
  );

  useBarcodeScanner(
    useCallback(
      (ean) => {
        navigation.replace("Product", {
          screen: "ProductMain",
          params: {
            ean,
            type: "ean",
            state:
              state === "expireDates" || state === "expireDates-add"
                ? "expireDates-add"
                : state,
          },
        });
      },
      [navigation, state]
    )
  );

  const StickyHeader = useMemo(() => {
    return (
      <View>
        <TabBar
          tabs={[
            {
              title: (
                <FormattedMessage
                  defaultMessage="Information"
                  description="Product Information tab title"
                />
              ),
              key: "details",
              badge: null,
            },
            {
              title: intl_tokens.ExpireDates,
              key: "expireDates",
              badge: (
                <Box
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  {product.hasPendingExpireDateActions ? (
                    <AHIcon
                      type="clock"
                      size="small"
                      color={
                        state === "expireDates" ? "text-inverted" : "primary"
                      }
                      spaceLeft="xsmall"
                    />
                  ) : null}
                  {product.hasExpiredExpireDates ? (
                    <AHIcon
                      type="flag"
                      size="small"
                      color={
                        state === "expireDates" ? "text-inverted" : "negative"
                      }
                      spaceLeft="xsmall"
                    />
                  ) : null}
                </Box>
              ),
            },
            {
              title: (
                <FormattedMessage
                  defaultMessage="Linked"
                  description="Linked tab title"
                />
              ),
              key: "linkedTodos",
              badge:
                product.numberOfPendingTodos > 0 ? (
                  <AHText
                    type="subtitle"
                    spaceLeft="xsmall"
                    color={
                      state === "linkedTodos" ? "text-inverted" : "text-primary"
                    }
                  >
                    {product.numberOfPendingTodos}
                  </AHText>
                ) : null,
            },
          ]}
          state={state}
          setState={(state) => {
            setState(state);
            scrollRef.current?.scrollTo({ y: 0, animated: true });
          }}
        />
        <AHSeparator />
      </View>
    );
  }, [product, state, setState, intl_tokens.ExpireDates, scrollRef]);

  const overlay = useMemo(() => {
    switch (state) {
      case "details":
        return null;
      case "linkedTodos":
        return (
          <ProductLinkedTodosFloatingAddButton
            store={store}
            addTodo={(categoryId) => {
              navigation.navigate("TodoCreate", {
                categoryId,
                teamIds: [],
                title: "",
                productIds: [product.id],
              });
            }}
          />
        );
      case "expireDates":
        return (
          <FloatingAddButton
            onPress={() => {
              setState("expireDates-add");
            }}
          />
        );
      case "expireDates-add":
        return (
          <ExpireDatePicker
            visible={state === "expireDates-add"}
            productId={product.id}
            onClose={() => {
              setState("expireDates");
            }}
            onCompleted={() => {
              setState("expireDates");
            }}
          />
        );
    }
  }, [state, product, setState, navigation, store]);

  const content = useMemo(() => {
    switch (state) {
      case "details":
        return (
          <ProductDetails
            key="productDetails"
            description={product.description ?? undefined}
            productId={product.id}
          />
        );
      case "linkedTodos":
        return (
          <ProductLinkedTodos
            key="productLinkedTodos"
            ref={linkedTodosRef}
            navigation={navigation}
            productId={product.id}
          />
        );
      case "expireDates-add":
      case "expireDates":
        return (
          <ProductExpireBatches
            ref={expireBatchesRef}
            key="productExpireDates"
            productId={product.id}
            onPressProductExpireBatch={(id) => {
              navigation.push("ProductExpireBatch", {
                id,
              });
            }}
            onPressAdd={() => {
              setState("expireDates-add");
            }}
          />
        );
    }
  }, [state, product, setState, navigation, expireBatchesRef, linkedTodosRef]);

  const onEndReached = useCallback(() => {
    if (state === "expireDates") {
      expireBatchesRef.current?.onEndReached();
    }
    if (state === "linkedTodos") {
      linkedTodosRef.current?.onEndReached();
    }
  }, [state]);

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      const scrollY = event.contentOffset.y;
      const visibleHeight = event.layoutMeasurement.height;
      const contentHeight = event.contentSize.height;

      const distanceFromBottom = contentHeight - (scrollY + visibleHeight);

      if (distanceFromBottom < 100) {
        runOnJS(onEndReached)();
      }
    },
  });

  return (
    <>
      <Animated.ScrollView
        ref={scrollRef}
        stickyHeaderIndices={[1]}
        contentContainerStyle={{
          paddingBottom: insets.bottom + layout.space.xxlarge,
        }}
        scrollEventThrottle={16}
        onScroll={scrollHandler}
      >
        <ProductHeader product={product} />
        {StickyHeader}
        {content}
      </Animated.ScrollView>
      {overlay}
    </>
  );
}

function ProductComponent(props: {
  fetchKey: string;
  navigation: any;
  id: string | null;
  ean: string | null;
  state: ProductScreenState;
  setState: (state: ProductScreenState) => void;
}) {
  const data = useLazyLoadQuery<ProductComponentQuery>(
    STORE_PRODUCT_QUERY,
    { id: props.id ?? "", ean: props.ean ?? "", isEan: props.ean !== null },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const product = data.currentStore?.productByEAN ?? data.storeProduct ?? null;
  if (!product) {
    return (
      <AHEmptyScreen
        title={
          <FormattedMessage
            defaultMessage="Product not found"
            description="Product Empty Screen title"
          />
        }
        subtitle={
          <FormattedMessage
            defaultMessage="We couldn’t match this EAN code to any products."
            description="Product Empty Screen subtitle"
          />
        }
        highlight={
          props.ean ? (
            <AHText
              type="title"
              spaceTop="medium"
              style={{
                textAlign: "center",
              }}
              selectable
            >
              {props.ean}
            </AHText>
          ) : null
        }
        illustration={
          <Box style={{ flex: 1 }}>
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={require("./NoProduct.png")}
            />
          </Box>
        }
      />
    );
  }
  return (
    <InnerProductComponent
      store={data.currentStore}
      navigation={props.navigation}
      state={props.state}
      product={product}
      setState={props.setState}
      ean={props.ean}
      id={props.id}
    />
  );
}

function ProductMain(props: ProductStackScreenProps<"ProductMain">) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => (
        <ProductComponent
          ean={
            props.route.params.type === "ean" ? props.route.params.ean : null
          }
          id={props.route.params.type === "id" ? props.route.params.id : null}
          state={props.route.params.state}
          setState={(state) => {
            props.navigation.setParams({ state });
          }}
          fetchKey={fk}
          navigation={props.navigation}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export const Product = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"Product">) => {
    const getOptions = useNavigationHeaderOptions();

    return (
      <ActionSheetProvider>
        <ProductStack.Navigator>
          <ProductStack.Screen
            name="ProductMain"
            component={ProductMain}
            options={({ route }) => ({
              ...getOptions({
                type: "CLOSE",
                headerTitle: DEVICE_HAS_SCANNER
                  ? () =>
                      route.params.state === "expireDates-add" ? (
                        <ScanToAddExpireDate />
                      ) : (
                        <ScanToOpenProductLabel />
                      )
                  : "",
                onClose: () => props.navigation.goBack(),
              }),
              headerShadowVisible: false,
            })}
          />
        </ProductStack.Navigator>
      </ActionSheetProvider>
    );
  },
  true
);
